/* Quick filter pills for collection pages.
 *
 * The pills are plain links, so they keep working without JS. When the theme's
 * filter drawer is on the page we hand the click over to it instead — clicking
 * the matching filter label runs the existing AJAX refresh, keeps the drawer,
 * the active tags and the URL in sync, and avoids a full page reload. */

class CollectionQuickFilters extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.quick-filters--scroller');
    this.pills = Array.from(this.querySelectorAll('.quick-filters--pill'));

    if (!this.pills.length) return;

    this.onClick = this.onClick.bind(this);
    this.sync = this.sync.bind(this);
    this.setOverflow = this.setOverflow.bind(this);

    this.onTagRemove = this.onTagRemove.bind(this);

    this.addEventListener('click', this.onClick);
    window.addEventListener('theme:navigation:reloaded', this.sync);

    /* Capture phase, so this runs before the theme's own handler on the tag and
     * can stop it — one removal path instead of two racing each other. */
    document.addEventListener('click', this.onTagRemove, true);
    document.addEventListener('keydown', this.onTagRemove, true);

    if (this.scroller) {
      this.scroller.addEventListener('scroll', this.setOverflow, { passive: true });
      window.addEventListener('resize', this.setOverflow, { passive: true });
      this.setOverflow();
      this.scrollToActive();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('theme:navigation:reloaded', this.sync);
    document.removeEventListener('click', this.onTagRemove, true);
    document.removeEventListener('keydown', this.onTagRemove, true);

    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.setOverflow);
      window.removeEventListener('resize', this.setOverflow);
    }
  }

  /* The drawer is relocated at runtime, so look it up on every use. */
  get inputs() {
    const filter = document.querySelector('.filter--root');
    if (!filter) return [];

    return Array.from(filter.querySelectorAll('[data-type="refine"] .filter--input'));
  }

  inputFor(param, value) {
    return this.inputs.find((input) => input.name === param && input.value === value) || null;
  }

  checkedInputsFor(param) {
    return this.inputs.filter((input) => input.name === param && input.checked);
  }

  onClick(event) {
    if (event.defaultPrevented || event.button > 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const pill = event.target.closest('.quick-filters--pill');
    if (!pill || !this.contains(pill)) return;

    const { param, value, clear } = pill.dataset;
    if (!param) return;

    const handled = clear === 'true' ? this.clearFilter(param) : this.toggleValue(param, value);
    if (!handled) return;

    event.preventDefault();
  }

  /* The theme renders its own "Fint x" tag under the filters. Removing it there
   * goes through the same uncheck-and-click-the-label path the pills use, so the
   * two controls cannot disagree. Only tags for this section's own filter are
   * taken over; every other tag keeps the theme's behaviour. */
  onTagRemove(event) {
    if (event.defaultPrevented) return;
    if (event.type === 'keydown' && event.key !== 'Enter') return;

    const param = this.dataset.param;
    if (!param) return;

    const tag = event.target.closest && event.target.closest('.navigation--active-tag[data-name]');
    if (!tag || tag.getAttribute('data-name') !== param) return;

    /* Bail before stopping the event if there is nothing to remove, so the
     * theme's handler still gets its chance. */
    const input = this.inputFor(param, tag.getAttribute('data-value'));
    if (!input || !input.checked || !input.nextElementSibling) return;

    event.preventDefault();
    event.stopPropagation();
    input.nextElementSibling.click();
  }

  get isSingleSelect() {
    return this.dataset.selection === 'single';
  }

  /* Toggling a value is just a click on the matching label: the label flips its
   * own checkbox and the theme picks it up from there. In single select the
   * other values are dropped first, so one click switches instead of stacking.
   * Clicking the active pill still turns it off. */
  toggleValue(param, value) {
    const input = this.inputFor(param, value);
    if (!input || !input.nextElementSibling) return false;

    if (this.isSingleSelect && !input.checked) {
      this.checkedInputsFor(param).forEach((other) => {
        if (other !== input) other.checked = false;
      });
    }

    input.nextElementSibling.click();
    return true;
  }

  /* Uncheck everything but the last active value, then click that one so a
   * single refresh clears the whole filter. */
  clearFilter(param) {
    const checked = this.checkedInputsFor(param);
    if (!checked.length) return this.inputs.length > 0;

    const last = checked.pop();
    if (!last.nextElementSibling) return false;

    checked.forEach((input) => {
      input.checked = false;
    });

    last.nextElementSibling.click();
    return true;
  }

  sync() {
    const inputs = this.inputs;
    if (!inputs.length) return;

    this.pills.forEach((pill) => {
      const { param, value, clear } = pill.dataset;
      if (!param) return;

      const active =
        clear === 'true'
          ? this.checkedInputsFor(param).length === 0
          : Boolean(this.inputFor(param, value)?.checked);

      pill.dataset.active = active;

      if (active) {
        pill.setAttribute('aria-current', 'true');
      } else {
        pill.removeAttribute('aria-current');
      }

      pill.setAttribute('href', this.buildHref(param, value, clear === 'true', active));
    });

    this.setOverflow();
  }

  /* Keep the no-JS fallback href pointing somewhere sensible after an AJAX
   * refresh, so opening a pill in a new tab still lands on the right view. */
  buildHref(param, value, isClear, active) {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (isClear) {
      params.delete(param);
    } else if (this.isSingleSelect) {
      params.delete(param);
      if (!active) params.append(param, value);
    } else {
      const values = params.getAll(param).filter((entry) => entry !== value);
      params.delete(param);
      values.forEach((entry) => params.append(param, entry));

      if (!active) params.append(param, value);
    }

    params.delete('page');

    const query = params.toString();
    return `${url.pathname}${query ? `?${query}` : ''}`;
  }

  setOverflow() {
    if (!this.scroller) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
    const start = scrollLeft > 1;
    const end = scrollLeft + clientWidth < scrollWidth - 1;
    let overflow = 'false';

    if (start && end) overflow = 'both';
    else if (start) overflow = 'start';
    else if (end) overflow = 'end';

    this.scroller.setAttribute('data-overflow', overflow);
  }

  scrollToActive() {
    const active = this.querySelector('.quick-filters--pill[data-active="true"]');
    if (!active || this.scroller.scrollWidth <= this.scroller.clientWidth) return;

    const offset = active.offsetLeft - (this.scroller.clientWidth - active.offsetWidth) / 2;
    this.scroller.scrollTo({ left: Math.max(offset, 0), behavior: 'instant' });
  }
}

if (!customElements.get('collection-quick-filters')) {
  customElements.define('collection-quick-filters', CollectionQuickFilters);
}
